import React, { useState } from 'react';
import { Eye, EyeSlash, FacebookLogo, GoogleLogo, ArrowLeft, Buildings, House } from '@phosphor-icons/react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Spinner } from '../components/ui/Spinner';
import { ImageUpload } from '../components/forms/ImageUpload';
import { StepRole } from '../components/forms/onboarding/StepRole';
import { PAGE_ROUTE } from '../lib/routes';
import { setLastLoginMethod, setSession, UserRole, getPendingIntent, clearPendingIntent } from '../lib/utils';
type Step = 'account' | 'profile' | 'role';
type Method = 'facebook' | 'google' | 'email';
interface RegisterProps {
  navigate: (page: string, context?: any) => void;
  context?: {
    presetRole?: UserRole;
  };
}
export function Register({
  navigate,
  context
}: RegisterProps) {
  // When AuthGate redirects here from a bookmark/offer intent, the role is
  // implicit (supply). We skip the role-selection step entirely.
  const presetRole = context?.presetRole;
  const [step, setStep] = useState<Step>('account');
  const [method, setMethod] = useState<Method | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [avatar, setAvatar] = useState('');
  const [role, setRole] = useState<UserRole | null>(presetRole ?? null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // 2 dots when role is preset (account → profile), else 3 dots
  const totalSteps = presetRole ? 2 : 3;
  const stepIndex = step === 'account' ? 0 : step === 'profile' ? 1 : 2;
  const handleSocial = (m: 'facebook' | 'google') => {
    setMethod(m);
    setStep('profile');
  };
  const handleEmailContinue = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    setMethod('email');
    setStep('profile');
  };
  const finalize = async (selectedRole: UserRole) => {
    setRole(selectedRole);
    setLoading(true);
    setError('');
    try {
      await new Promise(resolve => setTimeout(resolve, 600));
      if (method) setLastLoginMethod(method);
      setSession({
        name,
        email: email || undefined,
        phone: phone || undefined,
        avatar: avatar || undefined,
        role: selectedRole,
        hasListing: false
      });
      // Consume any pending intent (auth gate from TenantAnunt etc.) and
      // bounce back to where the user came from with the right modal open.
      const intent = getPendingIntent();
      if (intent) {
        clearPendingIntent();
        navigate(PAGE_ROUTE.TENANT_ANUNT, {
          slug: intent.tenantSlug,
          openApplyModal: intent.type === 'send_offer'
        });
        return;
      }
      if (selectedRole === 'demand') {
        navigate(PAGE_ROUTE.DASHBOARD_PROFIL);
      } else {
        navigate(PAGE_ROUTE.MARKETPLACE);
      }
    } catch {
      setError('A apărut o eroare. Te rugăm să încerci din nou.');
      setLoading(false);
    }
  };
  const handleProfileContinue = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name) return;
    if (presetRole) {
      // Skip role step — finalize directly with the preset role
      finalize(presetRole);
    } else {
      setStep('role');
    }
  };
  const handleRoleSelect = async (selectedRole: UserRole) => {
    await finalize(selectedRole);
  };
  const canGoBack = step !== 'account';
  const handleBack = () => {
    if (loading) return;
    if (step === 'profile') setStep('account');else if (step === 'role') setStep('profile');
  };
  return <div className="w-full max-w-md mx-auto px-4 py-12" data-id="element-3896">
      {/* Top row — back arrow (left) + progress dots (right) */}
      <div className="flex items-center justify-between gap-3 mb-8" data-id="element-3897">
        {canGoBack ? <button type="button" onClick={handleBack} disabled={loading} className="inline-flex items-center gap-2 text-sm text-light-3 hover:text-light-1 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed" aria-label="Înapoi" data-id="element-3898">
            <ArrowLeft size={16} data-id="element-3899" />
            Înapoi
          </button> : <span aria-hidden="true" data-id="element-3900" />}
        <div className="flex items-center gap-2" data-id="element-3901">
          {Array.from({
          length: totalSteps
        }).map((_, i) => <span key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i === stepIndex ? 'w-8 bg-lime-1' : i < stepIndex ? 'w-6 bg-lime-1' : 'w-6 bg-dark-4'}`} data-id="element-3902" />)}
        </div>
      </div>

      {/* Role-context badge — shown only when role is preset */}
      {presetRole && step === 'account' && <div className="mb-6 flex items-center justify-center" data-id="element-3903">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-lime-5 border border-lime-4 text-xs font-medium text-lime-1" data-id="element-3904">
            {presetRole === 'supply' ? <>
                <Buildings size={14} weight="fill" data-id="element-3905" />
                Cont de proprietar / agent
              </> : <>
                <House size={14} weight="fill" data-id="element-3906" />
                Cont de chiriaș
              </>}
          </span>
        </div>}

      {step === 'account' && <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-3907">
          <div className="text-center" data-id="element-3908">
            <h1 className="text-2xl font-bold text-light-1" data-id="element-3909">
              {presetRole === 'supply' ? 'Creează cont proprietar' : 'Creează cont'}
            </h1>
            <p className="text-sm text-light-3 mt-2" data-id="element-3910">
              Ai deja cont?{' '}
              <button onClick={() => navigate(PAGE_ROUTE.LOGIN)} className="font-medium text-lime-1 hover:text-lime-2 cursor-pointer" data-id="element-3911">
                Autentifică-te
              </button>
            </p>
          </div>

          <div className="space-y-4" data-id="element-3912">
            <Button onClick={() => handleSocial('facebook')} className="w-full h-12 bg-[#1877F2]/10 hover:bg-[#1877F2]/20 text-light-1 border border-[#1877F2]/30" data-id="element-3913">
              <FacebookLogo weight="fill" className="w-5 h-5 mr-3 text-[#1877F2]" data-id="element-3914" />
              Continuă cu Facebook
            </Button>

            <Button onClick={() => handleSocial('google')} className="w-full h-12 bg-dark-2 hover:bg-dark-3 text-light-1 border border-dark-3" data-id="element-3915">
              <GoogleLogo weight="bold" className="w-5 h-5 mr-3" data-id="element-3916" />
              Continuă cu Google
            </Button>

            <div className="relative py-4" data-id="element-3917">
              <div className="absolute inset-0 flex items-center" data-id="element-3918">
                <span className="w-full border-t border-dark-3" data-id="element-3919" />
              </div>
              <div className="relative flex justify-center text-xs uppercase" data-id="element-3920">
                <span className="bg-dark-1 px-2 text-light-3" data-id="element-3921">
                  sau cu email
                </span>
              </div>
            </div>

            <form onSubmit={handleEmailContinue} className="space-y-4" data-id="element-3922">
              <Input type="email" placeholder="Adresa de email" value={email} onChange={e => setEmail(e.target.value)} className="h-12 bg-dark-2 border-dark-3" required autoComplete="email" data-id="element-3923" />
              <div className="relative" data-id="element-3924">
                <Input type={showPassword ? 'text' : 'password'} placeholder="Parolă (min. 8 caractere)" value={password} onChange={e => setPassword(e.target.value)} className="h-12 bg-dark-2 border-dark-3 pr-12" required minLength={8} autoComplete="new-password" data-id="element-3925" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-light-3 hover:text-light-1 p-1" aria-label={showPassword ? 'Ascunde parola' : 'Arată parola'} data-id="element-3926">
                  {showPassword ? <EyeSlash className="w-5 h-5" data-id="element-3927" /> : <Eye className="w-5 h-5" data-id="element-3928" />}
                </button>
              </div>

              <Button type="submit" className="w-full h-12 bg-lime-1 text-dark-1 hover:bg-lime-2 font-semibold text-base mt-2" data-id="element-3929">
                Continuă
              </Button>
            </form>
          </div>

          <p className="text-xs text-light-3 text-center leading-relaxed" data-id="element-3930">
            Utilizarea platformei Izio este guvernată de{' '}
            <button onClick={() => navigate(PAGE_ROUTE.TERMENI)} className="text-light-2 underline hover:text-lime-1" data-id="element-3931">
              Termenii și Condițiile
            </button>{' '}
            și{' '}
            <button onClick={() => navigate(PAGE_ROUTE.CONFIDENTIALITATE)} className="text-light-2 underline hover:text-lime-1" data-id="element-3932">
              Politica de Confidențialitate
            </button>
            . Prin continuare, confirmi că ești de acord cu toți Termenii
            aplicabili și că ai luat la cunoștință Politica de
            Confidențialitate.
          </p>
        </div>}

      {step === 'profile' && <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-3933">
          <div className="text-center" data-id="element-3934">
            <h1 className="text-2xl font-bold text-light-1" data-id="element-3935">Profilul tău</h1>
            <p className="text-sm text-light-3 mt-2" data-id="element-3936">
              Adaugă câteva detalii pentru a fi cunoscut.
            </p>
          </div>

          {error && <div className="p-3 rounded-lg bg-red-4 border border-red-2 text-red-1 text-sm text-center" data-id="element-3937">
              {error}
            </div>}

          <form onSubmit={handleProfileContinue} className="space-y-6" data-id="element-3938">
            <div className="flex justify-center" data-id="element-3939">
              <ImageUpload value={avatar} onChange={setAvatar} data-id="element-3940" />
            </div>

            <div className="space-y-4" data-id="element-3941">
              <div className="space-y-1.5" data-id="element-3942">
                <label htmlFor="reg-name" className="block text-sm font-medium text-light-2" data-id="element-3943">
                  Nume complet *
                </label>
                <Input id="reg-name" value={name} onChange={e => setName(e.target.value)} placeholder="ex: Andrei Popescu" className="bg-dark-2 border-dark-3" required autoComplete="name" data-id="element-3944" />
              </div>

              <div className="space-y-1.5" data-id="element-3945">
                <label htmlFor="reg-phone" className="block text-sm font-medium text-light-2" data-id="element-3946">
                  Telefon (opțional)
                </label>
                <Input id="reg-phone" type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="ex: 0722 123 456" className="bg-dark-2 border-dark-3" autoComplete="tel" data-id="element-3947" />
                <p className="text-xs text-light-3" data-id="element-3948">
                  Util pentru contact direct la finalizarea unei oferte.
                </p>
              </div>
            </div>

            <Button type="submit" disabled={!name || loading} className="w-full h-12 bg-lime-1 text-dark-1 hover:bg-lime-2 font-semibold text-base disabled:opacity-50" data-id="element-3949">
              {loading ? <span className="inline-flex items-center gap-2" data-id="element-3950">
                  <Spinner size="sm" data-id="element-3951" />
                  Se finalizează contul...
                </span> : presetRole ? 'Finalizează' : 'Continuă'}
            </Button>
          </form>
        </div>}

      {step === 'role' && !presetRole && <div className="space-y-6" data-id="element-3952">
          {error && <div className="p-3 rounded-lg bg-red-4 border border-red-2 text-red-1 text-sm text-center" data-id="element-3953">
              {error}
            </div>}

          <StepRole value={role} onChange={handleRoleSelect} data-id="element-3954" />

          {loading && <div className="flex items-center justify-center gap-2 text-sm text-light-3" data-id="element-3955">
              <Spinner size="sm" data-id="element-3956" />
              Se finalizează contul...
            </div>}
        </div>}
    </div>;
}